import React, { useState } from 'react';
import { useAuth } from '@/context/auth';
import { useRestaurantImages } from '@/hooks/useRestaurantMedia';
import { FileUploadDropzone } from '../../FileUploadDropzone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, Upload } from 'lucide-react';

export const UploadTab: React.FC = () => {
  const { user } = useAuth();
  const { uploadImageAsync, isUploading } = useRestaurantImages(user!.organisationId);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<{current: number; total: number} | null>(null);
  const [dropzoneKey, setDropzoneKey] = useState(0); // Key to force remount after upload

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !user?.organisationId) return;

    // Parse tags (comma-separated)
    const tagArray = tags
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    // Upload files sequentially with progress tracking
    setUploadProgress({ current: 0, total: selectedFiles.length });

    for (let i = 0; i < selectedFiles.length; i++) {
      try {
        await uploadImageAsync({
          file: selectedFiles[i],
          organisation_id: user.organisationId,
          tags: tagArray.length > 0 ? tagArray : undefined
        });
        setUploadProgress({ current: i + 1, total: selectedFiles.length });
      } catch (error) {
        console.error(`Failed to upload ${selectedFiles[i].name}:`, error);
        // Continue with next file even if one fails
      }
    }

    // Reset form
    setSelectedFiles([]);
    setTags('');
    setUploadProgress(null);
    setDropzoneKey(prev => prev + 1); // Force FileUploadDropzone to remount and clear previews
  };

  const isProcessing = isUploading || uploadProgress !== null;
  const progressPercentage = uploadProgress
    ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Images</CardTitle>
          <CardDescription>
            Upload one or multiple images to your media library for use in email campaigns
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <FileUploadDropzone
              key={dropzoneKey}
              onFilesSelect={setSelectedFiles}
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              maxSize={10}
              multiple={true}
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags (optional)</Label>
            <Input
              id="tags"
              placeholder="promotion, summer, featured (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={isProcessing}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Add tags to help organize and find your images (applies to all images)
            </p>
          </div>

          {uploadProgress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Uploading images...</span>
                <span>{uploadProgress.current} / {uploadProgress.total}</span>
              </div>
              <Progress value={progressPercentage} />
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isProcessing}
            className="w-full"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading {uploadProgress?.current || 0} of {uploadProgress?.total || 0}...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {selectedFiles.length > 0 ? `${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}` : 'Images'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
